import React, { useEffect, useRef } from 'react';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const GoogleCallback = () => {
  const { setAuthData } = useAuth();
  const [status, setStatus] = React.useState('loading'); // loading, success, error
  const [message, setMessage] = React.useState('');
  const effectRun = useRef(false); // Protection stricte contre les doubles appels

  useEffect(() => {
    // Protection bulletproof contre React StrictMode et re-mounting
    if (effectRun.current) {
      console.log('Effect déjà exécuté, ignoré');
      return;
    }
    effectRun.current = true;

    const handleCallback = () => {
      try {
        // Log de debug: URL complète
        console.log('Current URL:', window.location.href);
        console.log('Search params:', window.location.search);

        // Récupération native et directe des paramètres depuis l'URL du navigateur
        const urlParams = new URLSearchParams(window.location.search);
        
        // Extraction des paramètres
        const token = urlParams.get('token');
        const userStr = urlParams.get('user');
        const role = urlParams.get('role');
        const error = urlParams.get('error') || urlParams.get('message');

        // Log de debug: Token trouvé
        console.log('Token found:', token ? 'YES' : 'NO');
        console.log('Token value:', token ? `${token.substring(0, 20)}...` : 'null');
        console.log('User string:', userStr);
        console.log('Role:', role);
        console.log('Error:', error);

        // Vérification d'erreur en premier
        if (error) {
          console.error('Error from callback:', error);
          setStatus('error');
          setMessage(decodeURIComponent(error));
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
          return;
        }

        // Vérification du token - SEULEMENT si manquant, afficher l'erreur
        if (!token) {
          console.error('Token manquant dans l\'URL');
          setStatus('error');
          setMessage('Tokens manquants. Veuillez réessayer.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
          return;
        }

        // Token présent - traitement du succès
        console.log('Token présent, traitement en cours...');

        // Décoder les données utilisateur si présentes
        let userData = null;
        if (userStr) {
          try {
            userData = JSON.parse(decodeURIComponent(userStr));
            console.log('User data decoded:', userData);
          } catch (parseError) {
            console.error('Erreur lors du parsing de user:', parseError);
            // Si le parsing échoue, créer un objet utilisateur minimal avec le role
            if (role) {
              userData = { role };
            }
          }
        } else if (role) {
          // Si pas de userStr mais un role, créer un objet minimal
          userData = { role };
        }

        // Utiliser la fonction setAuthData du contexte pour synchroniser l'état
        // Cela met à jour localStorage ET l'état React immédiatement
        setAuthData(token, userData);
        console.log('✅ Authentification synchronisée avec le contexte');

        // Déterminer le chemin de redirection selon le rôle
        let redirectPath = '/';
        if (role === 'admin') {
          redirectPath = '/admin/dashboard';
        } else if (role === 'vendor') {
          redirectPath = '/vendor/dashboard';
        } else if (role === 'user') {
          redirectPath = '/user/dashboard';
        }

        console.log('Redirection vers:', redirectPath);

        // Nettoyer l'URL (retirer les paramètres de query)
        window.history.replaceState({}, document.title, window.location.pathname);

        // Afficher le message de succès brièvement
        setStatus('success');
        setMessage('Connexion réussie ! Redirection...');

        // Redirection forcée avec window.location.href
        setTimeout(() => {
          window.location.href = redirectPath;
        }, 500);
      } catch (error) {
        console.error('Erreur lors du traitement du callback:', error);
        setStatus('error');
        setMessage('Une erreur est survenue. Veuillez réessayer.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      }
    };

    handleCallback();
  }, []); // Tableau de dépendances vide - s'exécute une seule fois

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-[#27489E] animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Connexion en cours...
            </h2>
            <p className="text-gray-500">
              Authentification avec Google en cours de traitement.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Connexion réussie !
            </h2>
            <p className="text-gray-500">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Erreur de connexion
            </h2>
            <p className="text-gray-500 mb-4">{message}</p>
            <button
              onClick={() => (window.location.href = '/login')}
              className="text-[#27489E] hover:text-[#1e3a7d] font-medium"
            >
              Retour à la connexion
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleCallback;

